import { Colors } from '@Constants';
import PropTypes from 'prop-types';
import React from 'react';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import { CurvedArrow, Spinner, ThumbsUpBubble, X } from '../icons/Icons';
import { font, media } from './style-utils';

const GlobalStyles = createGlobalStyle`
  html {
    /* font-size: 8px; */
    font-size: 10px;

    ${media.smallPhoneAndUp`
      /* font-size: 9px; */
      font-size: 10px;
    `}

    @media(min-width: 400px) {
      font-size: 10px;
    }

    ${media.tabletPortraitAndUp`
      font-size: 11px;
    `}

    ${media.tabletLandscapeAndUp`
      font-size: 10px;
    `}

    /* ${media.desktopAndUp`
      font-size: 9px;
    `} */

    ${media.mediumDesktopAndUp`
      font-size: 10px;
    `}

    ${media.largeDesktopAndUp`
      /* font-size: 11px; */
      font-size: 10px;
    `}

    /* ${media.hugeDesktopAndUp`
      font-size: 12px;
    `} */
  }

  body {
    min-width: 320px;
  }
`;

export default GlobalStyles;

export const Container = styled.div`
  margin: 0 auto;

  ${media.smallPhoneAndUp`
    max-width: 45rem;
  `}

  ${media.largePhoneAndUp`
    max-width: 55rem;
  `}

  ${media.tabletPortraitAndUp`
    max-width: 70rem;
  `}

  ${media.tabletLandscapeAndUp`
    max-width: 90rem;
  `}

  ${media.desktopAndUp`
    max-width: 140rem;
  `}
`;

export const ContentContainer = styled(Container)`
  padding: 0 2.5rem;
  position: relative;

  ${media.smallPhoneAndUp`
    padding: 0 3rem;
  `};
  ${media.largePhoneAndUp`
    padding: 0 5rem;
  `};
`;

export const FlexSection = styled.section`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  align-items: center;
  margin-top: ${(props) => props.marginTop || '0'};
  margin-bottom: ${(props) => props.marginBottom || '0'};
`;

export const CenteredFlexSection = styled(FlexSection)`
  justify-content: center;
  margin-top: ${(props) => props.marginTop || '0'};
  margin-bottom: ${(props) => props.marginBottom || '0'};
`;

// Buttons
export const Button = styled.button`
  align-items: center;
  background: none;
  border-radius: 99rem;
  border: none;
  display: inline-flex;
  justify-content: center;
  margin: 1rem;
  outline: none;
  overflow: hidden;
  user-select: none;
  box-sizing: border-box;
  height: 6rem;
  position: relative;
  width: auto;
  min-width: 25rem;
  max-width: 100%;
  height: 6rem;
  line-height: 6rem;
  margin: 0 auto;
  padding: 0 1.5em;
  white-space: nowrap;
  text-decoration: none !important;
  ${font(25, 'Bold', 25, -0.5)};
  transition: transform 0.1s ease-out, box-shadow 0.1s ease-out;
  box-shadow: 0 1.4rem 1.8rem 0 rgba(0, 0, 0, 0.16);
  cursor: pointer;
  z-index: 10;

  &:hover {
    color: inherit;
    transform: scale(1.02);
    box-shadow: 0 1.6rem 2rem 0.5rem rgba(0, 0, 0, 0.14);
  }

  &:active {
    transition: transform 0.05s ease-out, box-shadow 0.05s ease-out;
    transform: scale(0.98);
    box-shadow: 0 1rem 1rem -0.5rem rgba(0, 0, 0, 0.15);
  }

  & {
    ${(props) => props.noShadow && 'box-shadow: none !important;'};
  }

  ${(props) =>
    props.size === 'small' &&
    `
    font-size: 1.8rem;
    height: 4rem;
    line-height: 4rem;
    min-width: 15rem;
  `};
`;

const rotate = keyframes`
from {
  transform: rotate(0deg);
}
to {
  transform: rotate(360deg);
}
`;
const ButtonSpinner = styled(Spinner)`
  width: 1em;
  animation: ${rotate} 800ms linear infinite;
  display: block;
  width: 100%;
  height: 70%;
  position: absolute;
  top: 15%;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  color: currentColor;
  opacity: ${(props) => (props.isWorking ? 1 : 0)};
`;

const BaseButton = ({ children, isWorking, ...props }) => (
  <Button {...props}>
    <ButtonSpinner isWorking={isWorking} />
    <span style={{ opacity: isWorking ? 0 : 1 }}>{children}</span>
  </Button>
);

export const ButtonPrimary = styled(BaseButton)`
  background: ${Colors.Primary};
  color: #ffffff;

  &:hover,
  &:active,
  &:focus {
    color: #ffffff;
  }
`;

export const ButtonInverse = styled(BaseButton)`
  background: #ffffff;
  color: ${(props) => props.color || Colors.Primary};

  &:hover,
  &:active,
  &:focus {
    color: ${(props) => props.color || Colors.Primary};
  }
`;

export const ButtonTextLink = styled.button`
  background: none;
  appearance: none;
  border: 0;
  outline: none;
  color: inherit;
  padding: 0;
  font-weight: inherit;
  line-height: inherit;
  text-decoration: underline;
`;

export const SectionHeading = styled.div`
  ${font(42, 'Medium', 42, -0.75)};
  text-align: center;

  ${media.desktopAndUp`
    font-size: 5.6rem;
  `};
`;
export const SectionText = styled.div`
  color: ${Colors.PrimaryDark};
  p {
    ${font(20, 'Book', 28, -0.5)};
    color: inherit;
    margin-top: 1.4em;

    ${media.desktopAndUp`
      font-size: 2.2rem;
    `};
  }
`;

// Headings
export const H2 = styled.div`
  ${font(50, 'Black', 48.5, -2.5)};
  font-style: italic;
  margin: 0 0 1em;
  max-width: 22em;

  ${(props) => props.color && 'color: ' + props.color};

  ${media.desktopAndUp`
    font-size: 10rem;
  `};
`;

export const H3 = styled.div`
  font-family: 'National 2 Condensed';
  ${font(68, 'Bold', 56, -1.65)};
  margin: 0 0 1em;
  max-width: 8em;
  text-transform: uppercase;

  ${(props) => props.italic && 'font-style: italic'};
  ${(props) => props.color && 'color: ' + props.color};

  ${media.desktopAndUp`
    font-size: 10rem;
  `};
`;

export const H4 = styled.div`
  ${font(54, 'Black', 50, -2)};
  margin: 0 0 0.4em;
  max-width: 9em;
  color: inherit;

  ${(props) => props.color && 'color: ' + props.color};

  ${media.desktopAndUp`
    font-size: 8.2rem;
  `};
`;

export const H6 = styled.div`
  ${(props) => font(38, props.fontWeight ? props.fontWeight : 'Bold', 38, -1.14)};
  color: inherit;
  margin: 0 0 0.5em;
  max-width: 15em;

  ${(props) => props.fontStyle && 'font-style: ' + props.fontStyle};
  ${(props) => props.color && 'color: ' + props.color};

  ${media.desktopAndUp`
    font-size: 6rem;
  `};
`;

export const H7 = styled.div`
  max-width: 16em;
  ${(props) => font(37, props.fontWeight ? props.fontWeight : 'Medium', 40, -1.1)};
  color: inherit;
  margin: 0 0 1em;

  ${(props) => props.color && 'color: ' + props.color};

  ${media.desktopAndUp`
    /* max-width: 61rem; */
    font-size: 5rem;
  `};
`;

export const H8 = styled.div`
  max-width: 16em;
  ${(props) => font(28, props.fontWeight ? props.fontWeight : 'Medium', 30, -0.35)};
  color: inherit;
  margin: 0 0 1em;

  ${media.desktopAndUp`
    font-size: 3.8rem;
  `};
`;

// Paragraphs
export const P1 = styled.p`
  ${(props) => font(22, props.fontWeight ? props.fontWeight : 'Book', 26, -0.8)};
  margin: 0 0 1em;
  max-width: 20em;
  color: inherit;

  ${(props) => props.color && 'color: ' + props.color};

  ${media.desktopAndUp`
    font-size: 2.8rem;
  `};
`;

export const P2 = styled.p`
  ${(props) => font(16, props.fontWeight ? props.fontWeight : 'Book', 20, -0.5)};
  margin: 0 0 1em;
  max-width: 22em;
  color: inherit;

  ${(props) => props.color && 'color: ' + props.color};

  ${media.desktopAndUp`
    font-size: 2.2rem;
  `};

  ul {
    list-style: none;
    padding: 0;

    li::before {
      content: '•'; /* Unicode bullet symbol */
      /* Optional tweaks */
      font-weight: bold;
      position: absolute;
      left: 0;
      top: 0;
    }
  }

  ol {
    list-style: none;
    padding: 0;
    counter-reset: item;

    li::before {
      content: counter(item, decimal) '. ';
      counter-increment: item;
      /* Optional tweaks */
      font-weight: bold;
      position: absolute;
      left: 0;
      top: 0;
    }
  }

  li {
    position: relative;
    padding-left: 1.4em;
    margin: 0 0 1em;
  }
`;

export const P3 = styled.p`
  ${font(18, 'Book', 24, -0.75)};
  margin: 0 0 1em;
  max-width: 26em;
  color: #5a555f;
`;

export const P4 = styled.p`
  font-family: Calibre;
  ${(props) => font(22, props.fontWeight ? props.fontWeight : 'Regular', 30, -0.8)};
  margin: 0 0 1em;
  max-width: 22em;
  color: #302e34;

  ${(props) => props.color && 'color: ' + props.color};

  ${media.desktopAndUp`
    font-size: 2.8rem;
  `};
`;

export const P5 = styled.p`
  font-family: Calibre;
  ${font(16, 'Regular', 20, -0.5)};
  margin: 0 0 1em;
  max-width: 26em;

  ${(props) => props.color && 'color: ' + props.color};

  ${media.desktopAndUp`
    font-size: 2.2rem;
  `};
`;

// Label Styles
export const L1 = styled.div`
  ${font(24, 'Bold', 26, -0.5)};
  color: inherit;

  ${(props) => props.color && 'color: ' + props.color};

  ${media.desktopAndUp`
    /* font-size: 2.4rem; */
  `};
`;

export const L2 = styled.div`
  ${font(13, 'Black', 20, 5.78)};
  color: ${Colors.Primary};
  text-transform: uppercase;
  margin: 0 0 2em;

  ${(props) => props.color && 'color: ' + props.color};

  ${media.desktopAndUp`
    font-size: 1.8rem;
  `};
`;

export const L5 = styled.div`
  ${font(12, 'Medium', 15, 2)};
  margin: 0 0 0.4em;
  max-width: 22em;
  color: inherit;

  ${(props) => props.color && 'color: ' + props.color};

  ${media.desktopAndUp`
    font-size: 1.4rem;
  `};
`;

// Special Text
const Highlight = styled.span`
  position: relative;
  display: inline-block;

  &:before {
    content: '';
    background-color: ${(props) => props.highlightColor || 'var(--colorPrimaryMedium)'};
    display: block;
    height: ${(props) => props.height || '1em'};
    width: calc(100% + 0.25em);
    min-width: 1em;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -45%) skewX(-15deg);
    z-index: -1;
  }
`;

export const Highlighter = ({ children, ...otherProps }) => {
  if (!children) {
    return null;
  }
  let words = children.trim().split(' ');

  const highlightedText = words.map((word, index) => {
    return (
      <span key={`${word}-${index}`}>
        {' '}
        <Highlight {...otherProps}>{word}</Highlight>{' '}
      </span>
    );
  });
  return highlightedText;
};

export const Disclaimer = styled.p`
  ${font(16, 'Book', 21, 0)};
  color: inherit;
  text-transform: none;
  opacity: 0.7;
`;

// Images
export const Img = styled.img`
  width: 100%;
`;

// Product Label
const ProductLabelSpan = styled.span`
  display: inline-block;
  background: ${Colors.PrimaryLight2};
  border-radius: 0.9rem;
  margin-bottom: 1rem;
  position: relative;
  top: -2rem;

  ${media.desktopAndUp`
    top: -4rem;
  `};
`;

const ProductLabelL1 = styled(L1)`
  padding: 0.4em 0.75em;
  color: ${Colors.PrimaryDark};
`;

export const ProductLabel = ({ children, ...props }) => {
  return (
    <ProductLabelSpan {...props}>
      <ProductLabelL1>{children}</ProductLabelL1>
    </ProductLabelSpan>
  );
};

export const StandardSection = styled.section`
  ${ContentContainer} {
    & {
      padding-top: 4rem;
      padding-bottom: 4rem;
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;

      ${media.tabletLandscapeAndUp`
        padding-top: 8rem;
        padding-bottom: 8rem;
      `};

      ${ContentContainer} {
        padding-left: 0 !important;
        padding-right: 0 !important;
      }
    }
  }
`;

const InputWrapper = styled.div`
  margin-bottom: 1.5rem;
  width: 100%;
`;
const InputLabel = styled.label``;
const TextInput = styled.input`
  ${font(20, 'Black', 24, -0.8)};
  color: ${Colors.Primary};
  padding: 1.5rem 2rem;
  border-radius: 0.7rem;
  width: 100%;
  background: #f8f2ff;
  border: 2px solid transparent;
  text-align: left;
  height: 6rem;

  ${media.desktopAndUp`
    font-size: 2.8rem;
    height: 7.5rem;
    border-width: 3px;
  `};

  &:focus {
    background: #fff;
    border-color: #9f58f7;
  }

  &::-webkit-input-placeholder {
    /* Chrome/Opera/Safari */
    color: ${Colors.Primary};
    opacity: 0.35;
  }
  &::-moz-placeholder {
    /* Firefox 19+ */
    color: ${Colors.Primary};
    opacity: 0.35;
  }
  &:-ms-input-placeholder {
    /* IE 10+ */
    color: ${Colors.Primary};
    opacity: 0.35;
  }
  &:-moz-placeholder {
    /* Firefox 18- */
    color: ${Colors.Primary};
    opacity: 0.35;
  }
`;
export const Input = (props) => {
  return (
    <InputWrapper style={props.style}>
      {props.label && <InputLabel>{props.label}</InputLabel>}
      <TextInput {...props} />
    </InputWrapper>
  );
};

export const TextArea = (props) => {
  return (
    <InputWrapper>
      {props.label && <InputLabel>{props.label}</InputLabel>}
      <TextInput
        as="textarea"
        value={props.value}
        disabled={props.disabled}
        placeholder={props.placeholder}
        style={{ minHeight: '18rem' }}
        {...props}
      />
    </InputWrapper>
  );
};

// Styled Notch
const NotchShape = styled.div`
  background-color: ${(props) => props.color};
`;
const NotchContainer = styled.div`
  position: absolute;
  left: 50%;
  transform: scaleX(1.15);
  transform-origin: left;
  z-index: 1;

  ${NotchShape} {
    width: 2.7rem;
    height: 2.7rem;
    border-radius: 0.45rem 0;
    transform: translate(-50%, -50%) rotate(45deg);
    overflow: hidden;
  }
`;
const Notch = (props) => (
  <NotchContainer className={props.className}>
    <NotchShape color={props.color}>{props.children}</NotchShape>
  </NotchContainer>
);
export const SmallTopNotch = styled(Notch)`
  top: 0;

  ${NotchShape} {
    width: 2.7rem;
    height: 2.7rem;
    border-radius: 0.45rem 0;
    transform: translate(-50%, -50%) rotate(45deg);
  }
`;
export const SmallBottomNotch = styled(Notch)`
  bottom: 0;

  ${NotchShape} {
    width: 2.7rem;
    height: 2.7rem;
    border-radius: 0.45rem 0;
    transform: translate(-50%, 50%) rotate(45deg);
  }
`;
export const TopNotch = styled(Notch)`
  top: 0;

  ${NotchShape} {
    width: 3.5rem;
    height: 3.5rem;
    border-radius: 0.45rem 0;
    transform: translate(-50%, -50%) rotate(45deg);
  }
`;
export const BottomNotch = styled(Notch)`
  bottom: 0;

  ${NotchShape} {
    width: 3.5rem;
    height: 3.5rem;
    border-radius: 0.45rem 0;
    transform: translate(-50%, 50%) rotate(45deg);
  }
`;

// Cards
const CardSection = styled(StandardSection)`
  margin: 0 2rem;

  ${ContentContainer} {
    & {
      text-align: center;
      background-color: #000000;
      color: #ffffff;
      position: relative;
      z-index: 1;
      border-radius: 3.5rem;
      padding: 8rem 3.5rem 4rem;

      ${media.tabletLandscapeAndUp`
        padding: 12rem 8rem 8rem;
      `};
    }
  }
`;
export const Card = ({ children, ...props }) => (
  <CardSection {...props}>
    <ContentContainer>{children}</ContentContainer>
  </CardSection>
);

export const TestimonialGridWrapper = styled.div`
  background: #f9f2ff;
  padding-top: 20rem;
  padding-bottom: 4rem;
  margin-top: -20rem;
`;

export function TestimonialGridHeader(props) {
  return (
    <TestimonialGridHeaderLayout>
      <TestimonialGridHeaderThumbsUp>
        <ThumbsUpBubble />
      </TestimonialGridHeaderThumbsUp>
      <TestimonialGridHeaderHeadline>
        <TestimonialGridHeaderAdjacent>{props.preText}</TestimonialGridHeaderAdjacent>{' '}
        {props.highlightText.split(' ').map((word, index) => (
          <span key={index}>
            <TestimonialGridHeaderHighlighted>
              <span>{word} </span>
            </TestimonialGridHeaderHighlighted>{' '}
          </span>
        ))}
      </TestimonialGridHeaderHeadline>
      <TestimonialGridHeaderArrow>
        <CurvedArrow />
      </TestimonialGridHeaderArrow>
    </TestimonialGridHeaderLayout>
  );
}
TestimonialGridHeader.propTypes = {
  preText: PropTypes.node,
  highlightText: PropTypes.node,
};
TestimonialGridHeader.defaultProps = {
  preText: 'preText',
  highlightText: 'highlightText',
};

const TestimonialGridHeaderLayout = styled.div`
  padding-top: 4rem;
  text-align: center;
`;

const TestimonialGridHeaderThumbsUp = styled.div`
  display: inline-block;
  width: 7.8rem;

  @media (min-width: 638px) {
    & > svg {
      position: relative;
      z-index: 1;
    }
  }
`;

const TestimonialGridHeaderArrow = styled(H7)`
  text-align: right;
  padding-right: 10%;
  margin: 0 auto;

  svg {
    max-width: 20%;
    height: auto;
  }
`;

const TestimonialGridHeaderHeadline = styled(H7)`
  max-width: 100%;
  font-weight: 800;
  margin-top: 1.5rem;
  margin-left: auto;
  margin-right: auto;
`;

// Keeps non-highlighted text about highlight background
const TestimonialGridHeaderAdjacent = styled.span`
  position: relative;
  z-index: 2;
`;
const TestimonialGridHeaderHighlighted = styled.span`
  position: relative;
  display: inline-block;

  &::before {
    content: '';
    background: #fff;
    transform: translateX(-0.1em) skewX(-10deg);
    width: calc(106% + 0.2em);
    height: 100%;
    position: absolute;
    left: -3%;
    top: 0;
    z-index: 0;
  }

  & > span {
    color: black;
    position: relative;
    z-index: 1;
  }
`;

const OverlayContainer = styled(ContentContainer)`
  padding: 6rem 1.7rem 6rem;
  text-align: center;
  white-space: normal;
  display: inline-block;
  position: relative;
  vertical-align: middle;
  width: 100%;
  text-align: center;

  ${media.tabletLandscapeAndUp`
    /* border-radius: 3.5rem; */
    /* max-width: 58rem; */
    height: auto;
    padding: 6rem 8.6rem;
  `};
`;

const OverlayWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99001;
  height: 100%;
  width: 100%;
  white-space: nowrap;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background-color: #fbf7ff;
  padding: 0;

  ${media.tabletLandscapeAndUp`
    /* background: none; */
    padding: 4rem 0;
  `} &:after {
    content: '';
    display: inline-block;
    width: 1px;
    height: 100%;
    vertical-align: middle;
    margin-right: -1px;
  }
`;

const CloseButtonCircle = styled.div`
  opacity: 0.15;
  height: 4.4rem;
  width: 4.4rem;
  border-radius: 4.4rem;
  background: ${Colors.Primary};
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
`;

const CloseButtonX = styled(X)`
  width: 1.2rem;
  color: ${Colors.Primary};
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
`;

const CloseButtonContainer = styled.button`
  background: transparent;
  border-radius: 4.4rem;
  height: 4.4rem;
  width: 4.4rem;
  position: relative;
  z-index: 10;
  cursor: pointer;
  transition: transform 0.1s ease-out;

  &:hover {
    color: inherit;
    transform: scale(1.06);
    /* box-shadow: 0 1.6rem 2rem 0.5rem rgba(113, 112, 114, 0.23); */
  }

  &:active {
    transition: transform 0.05s ease-out;
    transform: scale(0.94);
    /* box-shadow: 0 1rem 1rem -0.5rem rgba(113, 112, 114, 0.26); */
  }
`;

const CloseButton = (props) => (
  <CloseButtonContainer {...props}>
    <CloseButtonCircle />
    <CloseButtonX />
  </CloseButtonContainer>
);

const OverlayCloseButton = styled(CloseButton)`
  position: absolute;
  top: 5vh;
  right: 10vw;
`;

const OverlayComp = (props) => {
  return (
    <OverlayWrapper className={props.className}>
      <OverlayCloseButton onClick={props.onClickCloseOverlay} />
      <OverlayContainer>{props.children}</OverlayContainer>
    </OverlayWrapper>
  );
};

export const Overlay = styled(OverlayComp)`
  padding: 10rem 0;

  & > div {
    max-width: 80rem;
    margin: 0 auto;
    padding: 0 3rem;
  }
`;



// WEBPACK FOOTER //
// ./src/components/GlobalStyles.js